import { useState } from "react";
import { Phone, Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { sendContact } from "@/lib/api";

const CONTACTS = [
    {
        icon: Phone,
        title: "Telefones",
        lines: ["(71) 99174-0168 — Jorge", "(71) 98882-8619 — Juan"],
    },
    {
        icon: Mail,
        title: "E-mails",
        lines: ["jorge@goodmaxgestao.com.br", "juan@goodmaxgestao.com.br"],
    },
    {
        icon: MapPin,
        title: "Endereço",
        lines: [
            "Rua Francisco Gonçalves, 01",
            "Edf. Reitor Miguel Calmon, Sala 702",
            "Comércio, Salvador - BA",
        ],
    },
];

export default function Contato() {
    const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email || !form.message) {
            toast.error("Preencha nome, e-mail e mensagem.");
            return;
        }
        setLoading(true);
        try {
            await sendContact(form);
            setSent(true);
            toast.success("Mensagem enviada! Retornaremos em breve.");
            setForm({ name: "", email: "", phone: "", subject: "", message: "" });
        } catch {
            toast.error("Não foi possível enviar. Tente novamente.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section
            id="contato"
            data-testid="contato-section"
            className="relative py-24 sm:py-32 border-t border-zinc-200 dark:border-white/5"
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <span className="inline-block text-xs font-bold uppercase tracking-[0.22em] text-[#E89546]">
                        Contato
                    </span>
                    <h2 className="font-heading mt-4 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-zinc-950 dark:text-white leading-tight">
                        Vamos conversar sobre a sua operação.
                    </h2>
                    <p className="mt-6 text-zinc-600 dark:text-zinc-400 leading-relaxed">
                        Nosso time comercial está pronto para entender a sua demanda e desenhar uma proposta sob medida.
                    </p>

                    <div className="mt-10 space-y-5">
                        {CONTACTS.map(({ icon: Icon, title, lines }) => (
                            <div
                                key={title}
                                data-testid={`contato-info-${title.toLowerCase()}`}
                                className="flex items-start gap-4 p-5 rounded-2xl border border-zinc-200 dark:border-white/5 bg-white dark:bg-[#0d0d0d] shadow-sm dark:shadow-none"
                            >
                                <span className="w-11 h-11 rounded-xl bg-[#2DA89E]/10 text-[#2DA89E] flex items-center justify-center shrink-0">
                                    <Icon className="w-5 h-5" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-zinc-500 dark:text-zinc-500">{title}</div>
                                    <div className="mt-1 text-zinc-950 dark:text-white space-y-0.5">
                                        {lines.map((l) => (
                                            <div key={l} className="text-sm sm:text-base">{l}</div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <form
                        data-testid="contato-form"
                        onSubmit={onSubmit}
                        className="rounded-3xl border border-zinc-200 dark:border-white/10 bg-white dark:bg-[#0d0d0d] p-6 sm:p-10 shadow-sm dark:shadow-none"
                    >
                        <h3 className="font-heading text-xl font-bold text-zinc-950 dark:text-white">Envie uma mensagem</h3>
                        <p className="text-sm text-zinc-600 dark:text-zinc-400 mt-1">Responderemos no menor prazo possível.</p>

                        <div className="grid sm:grid-cols-2 gap-4 mt-6">
                            <Field label="Nome *" name="name" value={form.name} onChange={onChange} testid="contato-input-name" />
                            <Field label="E-mail *" type="email" name="email" value={form.email} onChange={onChange} testid="contato-input-email" />
                            <Field label="Telefone" name="phone" value={form.phone} onChange={onChange} testid="contato-input-phone" />
                            <Field label="Assunto" name="subject" value={form.subject} onChange={onChange} testid="contato-input-subject" />
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-2">Mensagem *</label>
                                <textarea
                                    data-testid="contato-input-message"
                                    name="message"
                                    rows={5}
                                    value={form.message}
                                    onChange={onChange}
                                    placeholder="Conte-nos sobre sua necessidade..."
                                    className="w-full bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-xl px-4 py-3 text-zinc-950 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-[#E89546] focus:border-[#E89546] transition resize-none"
                                />
                            </div>
                        </div>

                        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
                            <button
                                data-testid="contato-submit-btn"
                                type="submit"
                                disabled={loading}
                                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#E89546] text-black font-semibold transition-transform hover:bg-[#f0a25b] active:scale-95 disabled:opacity-60"
                            >
                                {loading ? "Enviando..." : (<>Enviar mensagem <Send className="w-4 h-4" /></>)}
                            </button>
                            {sent && (
                                <span data-testid="contato-success" className="text-sm text-[#2DA89E] inline-flex items-center gap-2">
                                    <CheckCircle2 className="w-4 h-4" /> Mensagem recebida.
                                </span>
                            )}
                        </div>
                    </form>
                </div>
            </div>
        </section>
    );
}

function Field({ label, name, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="block text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-2">{label}</label>
            <input
                data-testid={testid}
                type={type}
                name={name}
                value={value}
                onChange={onChange}
                className="w-full bg-zinc-50 dark:bg-white/5 border border-zinc-200 dark:border-white/10 rounded-xl px-4 py-3 text-zinc-950 dark:text-white placeholder:text-zinc-400 dark:placeholder:text-zinc-500 focus:outline-none focus:ring-1 focus:ring-[#E89546] focus:border-[#E89546] transition"
            />
        </div>
    );
}
